import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  fetchSurveyDetail,
  getSurveyApiErrorMessage,
  shouldReconcileSurveySubmit,
  submitSurvey,
  type SurveyPublicDetail,
} from "../../api/survey";
import {
  getNewAnswers,
  getPrefilledAnswers,
  hasAnswer,
  type AnswersByQuestion,
} from "./answers";
import QuestionField from "./QuestionField";
import {
  captureSurveyAccessTokenFromLocation,
  clearStoredSurveyAccessToken,
  getStoredSurveyAccessToken,
} from "../../utils/surveyAccessUrl";

const isPositiveSafeInteger = (value: number): boolean =>
  Number.isSafeInteger(value) && value > 0;

const canStartAnotherRound = (detail: SurveyPublicDetail): boolean =>
  detail.responseLimit === null ||
  detail.completedResponseCount < detail.responseLimit;

export default function SurveyPage() {
  const { surveyId } = useParams<{ surveyId: string }>();
  const parsedSurveyId = Number(surveyId);
  captureSurveyAccessTokenFromLocation();
  // storageをrender毎に読むと、下記の終了時クリアでサンクス画面が壊れるためマウント時に確定させる
  const surveyToken = useMemo(
    () => getStoredSurveyAccessToken(parsedSurveyId),
    [parsedSurveyId],
  );
  const hasValidAccessParams =
    isPositiveSafeInteger(parsedSurveyId) && Boolean(surveyToken.trim());

  const [detail, setDetail] = useState<SurveyPublicDetail | null>(null);
  const [answers, setAnswers] = useState<AnswersByQuestion>({});
  const [loading, setLoading] = useState(true);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [thanksMessage, setThanksMessage] = useState<string | null>(null);
  const submitInFlightRef = useRef(false);

  useEffect(() => {
    let active = true;

    if (!hasValidAccessParams) {
      setDetail(null);
      setAnswers({});
      setLoadError("アクセスURLが不正です。");
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    setLoadError(null);
    setSubmitError(null);
    setThanksMessage(null);

    fetchSurveyDetail({
      surveyId: parsedSurveyId,
      surveyToken,
    })
      .then((data) => {
        if (!active) return;
        setDetail(data);
        setAnswers(getPrefilledAnswers(data.questions));
        if (data.isCompleted) {
          setThanksMessage(data.thanksMessage?.trim() || "回答済みです");
        }
      })
      .catch((error: unknown) => {
        if (!active) return;
        setDetail(null);
        setLoadError(
          getSurveyApiErrorMessage(
            error,
            "アンケートの読み込みに失敗しました。",
          ),
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [hasValidAccessParams, loadAttempt, parsedSurveyId, surveyToken]);

  useEffect(() => {
    // 共有ブラウザで後続の訪問者が前の回答者として認証されないよう、
    // これ以上回答できなくなった時点で保存済みトークンを破棄する
    if (detail && detail.isCompleted && !canStartAnotherRound(detail)) {
      clearStoredSurveyAccessToken(parsedSurveyId);
    }
  }, [detail, parsedSurveyId]);

  const requiredMissing = useMemo(() => {
    if (!detail) return false;
    return detail.questions.some(
      (question) =>
        question.required &&
        !question.answered &&
        !hasAnswer(question, answers[question.id]),
    );
  }, [answers, detail]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      !detail ||
      requiredMissing ||
      submitInFlightRef.current ||
      !hasValidAccessParams
    ) {
      return;
    }

    const newAnswers = getNewAnswers(detail.questions, answers);

    submitInFlightRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await submitSurvey({
        surveyId: parsedSurveyId,
        surveyToken,
        answers: newAnswers,
      });
      setThanksMessage(
        result.message?.trim() || "ご回答ありがとうございました。",
      );
      setDetail((current) =>
        current
          ? {
              ...current,
              isCompleted: true,
              completedResponseCount: current.completedResponseCount + 1,
            }
          : current,
      );
    } catch (error: unknown) {
      const errorMessage = getSurveyApiErrorMessage(
        error,
        "送信に失敗しました。",
      );

      if (shouldReconcileSurveySubmit(error)) {
        try {
          const latestDetail = await fetchSurveyDetail({
            surveyId: parsedSurveyId,
            surveyToken,
          });
          if (latestDetail.isCompleted) {
            setDetail(latestDetail);
            setAnswers(getPrefilledAnswers(latestDetail.questions));
            setThanksMessage(
              latestDetail.thanksMessage?.trim() || "回答済みです",
            );
            return;
          }
        } catch {
          setSubmitError(errorMessage);
          return;
        }
      }

      setSubmitError(errorMessage);
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="h-full min-h-0 overflow-y-auto bg-surface p-4">
        <div className="mx-auto flex max-w-lg items-center justify-center gap-2 py-16 text-muted">
          <span
            aria-hidden="true"
            className="h-4 w-4 animate-spin rounded-full border-2 border-accent border-t-transparent"
          />
          読み込み中...
        </div>
      </main>
    );
  }

  if (loadError || !detail) {
    return (
      <main className="h-full min-h-0 overflow-y-auto bg-surface p-4">
        <div className="mx-auto max-w-lg space-y-4 pt-8">
          <p
            className="whitespace-pre-line rounded-2xl border border-danger-border bg-danger-soft px-4 py-3 text-danger"
            role="alert"
          >
            {loadError ?? "エラー"}
          </p>
          {hasValidAccessParams ? (
            <button
              type="button"
              className="w-full rounded-full bg-[#06C755] px-4 py-3 font-semibold text-white transition active:opacity-80"
              onClick={() => setLoadAttempt((attempt) => attempt + 1)}
            >
              再試行
            </button>
          ) : null}
        </div>
      </main>
    );
  }

  if (thanksMessage) {
    return (
      <main className="h-full min-h-0 overflow-y-auto bg-surface">
        <div className="mx-auto max-w-lg p-6">
          <h1 className="sr-only">アンケート</h1>
          <div className="mt-8 grid justify-items-center gap-4 rounded-2xl border border-border/70 bg-white p-8 text-center shadow-sm">
            <span
              aria-hidden="true"
              className="flex h-14 w-14 items-center justify-center rounded-full bg-[#06C755] text-2xl font-bold text-white"
            >
              ✓
            </span>
            <p className="whitespace-pre-line text-base text-ink">
              {thanksMessage}
            </p>
            {detail && canStartAnotherRound(detail) ? (
              <button
                type="button"
                className="w-full rounded-full bg-[#06C755] px-4 py-3 font-semibold text-white transition active:opacity-80"
                onClick={() => {
                  setSubmitError(null);
                  setAnswers({});
                  setThanksMessage(null);
                  setDetail((current) =>
                    current
                      ? {
                          ...current,
                          isCompleted: false,
                          questions: current.questions.map((question) => ({
                            ...question,
                            answered: false,
                            answer: undefined,
                          })),
                        }
                      : current,
                  );
                }}
              >
                もう一度回答する
              </button>
            ) : null}
          </div>
        </div>
      </main>
    );
  }

  const totalQuestions = detail.questions.length;
  const remainingRequired = detail.questions.filter(
    (question) =>
      question.required &&
      !question.answered &&
      !hasAnswer(question, answers[question.id]),
  ).length;

  return (
    <main className="h-full min-h-0 overflow-y-auto bg-surface">
      <form
        data-testid="survey-form"
        className="mx-auto max-w-lg space-y-4 p-4 pb-10"
        noValidate
        onSubmit={handleSubmit}
      >
        <header className="space-y-1 pt-2">
          <h1 className="text-xl font-semibold text-ink">アンケート</h1>
          <p className="text-sm text-muted">
            全{totalQuestions}問です。「送信する」を押すと回答が確定します。
          </p>
        </header>
        {detail.questions.map((question, index) => (
          <QuestionField
            key={question.id}
            question={question}
            questionNumber={index + 1}
            answer={answers[question.id]}
            disabled={submitting}
            onChange={(answer) =>
              setAnswers((previous) => ({
                ...previous,
                [question.id]: answer,
              }))
            }
          />
        ))}
        {submitError ? (
          <p
            className="whitespace-pre-line rounded-2xl border border-danger-border bg-danger-soft px-4 py-3 text-danger"
            role="alert"
          >
            {submitError}
          </p>
        ) : null}
        <div className="space-y-2 pt-2">
          {requiredMissing ? (
            <p className="text-center text-sm text-muted" role="status">
              必須の設問が残り{remainingRequired}問あります
            </p>
          ) : null}
          <button
            type="submit"
            className="w-full rounded-full bg-[#06C755] px-4 py-3.5 text-base font-semibold text-white shadow-sm transition active:opacity-80 disabled:opacity-50"
            disabled={submitting || requiredMissing}
          >
            {submitting ? "送信中..." : "送信する"}
          </button>
        </div>
      </form>
    </main>
  );
}
